import type { DragEvent, ReactNode } from 'react';
import {
  Button,
  ListBox,
  ListBoxItem,
  Popover,
  Select,
  SelectValue,
  SelectionIndicator,
  ToggleButton,
  ToggleButtonGroup,
} from 'react-aria-components';
import { landingPresets } from '../landing/presets.js';
import type { RecordView } from '../state/record-view-preference.js';
import { viewerStrings } from '../copy/strings.js';
import { hasText, optionalText } from '../copy/text.js';
import { ReferenceLinks } from './ReferenceLinks.js';
import tableShot from '../landing/table.webp';
import filterShot from '../landing/filter.webp';
import sheetShot from '../landing/sheet.webp';
import chartsShot from '../landing/charts.webp';
import './landing.css';

const c = viewerStrings.webComponentsLanding;

interface ViewFigure {
  key: 'table' | 'filter' | 'sheet' | 'charts';
  wide: boolean;
  src: string;
  title: string;
  meta: string;
  caption: string;
  alt: string;
}

const figures: ViewFigure[] = [
  {
    key: 'table',
    wide: true,
    src: tableShot,
    title: c.tableTitle,
    meta: c.tableMeta,
    caption: c.tableCaption,
    alt: c.tableAlt,
  },
  {
    key: 'filter',
    wide: false,
    src: filterShot,
    title: c.filterTitle,
    meta: c.filterMeta,
    caption: c.filterCaption,
    alt: c.filterAlt,
  },
  {
    key: 'sheet',
    wide: true,
    src: sheetShot,
    title: c.sheetTitle,
    meta: c.sheetMeta,
    caption: c.sheetCaption,
    alt: c.sheetAlt,
  },
  {
    key: 'charts',
    wide: false,
    src: chartsShot,
    title: c.chartsTitle,
    meta: c.chartsMeta,
    caption: c.chartsCaption,
    alt: c.chartsAlt,
  },
];

const usage = [c.usageStep1, c.usageStep2, c.usageStep3, c.usageStep4, c.usageStep5, c.usageStep6];

/** A text-only row of the drawing sheet: a mono label beside its content. Collapses when both are blank. */
function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="land-row" data-copy-group>
      {hasText(label) ? <div className="land-label">{label}</div> : null}
      {children}
    </div>
  );
}

/** Choose the view that opens after import; the labels live inside the control. */
function ViewToggle({
  view,
  onChange,
}: {
  view: RecordView;
  onChange: (view: RecordView) => void;
}) {
  const all: { id: RecordView; label: string }[] = [
    { id: 'spreadsheet', label: c.viewSpreadsheet },
    { id: 'table', label: c.viewTable },
  ];
  const options = all.filter((option) => hasText(option.label));
  const naming = hasText(c.viewAsLabel)
    ? { 'aria-labelledby': 'land_view_as_label' }
    : { 'aria-label': c.viewAsAccessible };
  if (options.length < 2) return null;
  return (
    <div className="land-view-as" data-copy-group>
      {hasText(c.viewAsLabel) ? (
        <span className="land-view-as-label" id="land_view_as_label">
          {c.viewAsLabel}
        </span>
      ) : null}
      <ToggleButtonGroup
        className="land-toggle"
        selectionMode="single"
        disallowEmptySelection
        selectedKeys={[view]}
        onSelectionChange={(keys) => {
          const next = [...keys][0];
          if (next === 'table' || next === 'spreadsheet') onChange(next);
        }}
        {...naming}
      >
        {options.map((option) => (
          <ToggleButton key={option.id} id={option.id} className="land-toggle-option">
            <SelectionIndicator className="land-toggle-indicator" />
            <span className="land-toggle-text">{option.label}</span>
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </div>
  );
}

/** Choose the platform field preset applied when files open. */
function PresetSelect({ preset, onChange }: { preset: string; onChange: (id: string) => void }) {
  if (landingPresets.length === 0) return null;
  const naming = hasText(c.columnsLabel)
    ? { 'aria-labelledby': 'land_columns_label' }
    : { 'aria-label': c.columnsAccessible };
  return (
    <div className="land-view-as" data-copy-group>
      {hasText(c.columnsLabel) ? (
        <span className="land-view-as-label" id="land_columns_label">
          {c.columnsLabel}
        </span>
      ) : null}
      <Select
        className="land-select"
        selectedKey={preset}
        onSelectionChange={(key) => {
          if (typeof key === 'string') onChange(key);
        }}
        {...naming}
      >
        <Button className="land-select-button">
          <SelectValue className="land-select-value" />
          <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false">
            <path d="M3 6l5 5 5-5" fill="none" stroke="currentColor" strokeWidth="1.5"></path>
          </svg>
        </Button>
        <Popover className="land-select-popover" offset={4}>
          <ListBox className="land-select-list">
            {[...landingPresets]
              .sort((a, b) => a.title.localeCompare(b.title, 'en', { sensitivity: 'base' }))
              .map((item) => (
                <ListBoxItem key={item.id} id={item.id} className="land-select-item">
                  {item.title}
                </ListBoxItem>
              ))}
          </ListBox>
        </Popover>
      </Select>
    </div>
  );
}

/**
 * Drawing-sheet landing page shown until files are imported. The upload control is owned by the
 * router so one import path serves the hero, the sign-off and the workspace toolbar.
 */
export function Landing({
  upload,
  onDropFiles,
  view,
  onViewChange,
  preset,
  onPresetChange,
}: {
  upload: ReactNode;
  onDropFiles: (files: File[]) => void;
  view: RecordView;
  onViewChange: (view: RecordView) => void;
  preset: string;
  onPresetChange: (id: string) => void;
}) {
  const drop = (event: DragEvent<HTMLElement>) => {
    event.preventDefault();
    onDropFiles([...event.dataTransfer.files]);
  };
  return (
    <section
      className="land"
      onDragOver={(event) => event.preventDefault()}
      onDrop={drop}
      data-copy-group
    >
      <div className="land-sheet">
        <div className="land-hero" data-copy-group>
          {(hasText(c.title) || hasText(c.subtitle)) && (
            <h1 className="land-title">
              {hasText(c.title) ? <span className="land-title-line">{c.title}</span> : null}
              {hasText(c.subtitle) ? <span className="land-title-accent">{c.subtitle}</span> : null}
            </h1>
          )}
          <div className="land-manual">
            {hasText(c.description) ? (
              <Row label={c.descriptionLabel}>
                <p className="land-text">{c.description}</p>
              </Row>
            ) : null}
            {usage.some(hasText) ? (
              <Row label={c.usageLabel}>
                <ol className="land-steps">
                  {usage.filter(hasText).map((step) => (
                    <li key={step}>{step}</li>
                  ))}
                </ol>
              </Row>
            ) : null}
            <Row label={c.seeAlsoLabel}>
              <ReferenceLinks />
            </Row>
            <div className="land-row land-cta-row">
              <div className="land-label" aria-hidden="true"></div>
              <div className="land-cta">
                {upload}
                <ViewToggle view={view} onChange={onViewChange} />
                <PresetSelect preset={preset} onChange={onPresetChange} />
                {hasText(viewerStrings.webMain.filesStayInThisBrowserNothingIs) ? (
                  <span className="land-note">
                    {viewerStrings.webMain.filesStayInThisBrowserNothingIs}
                  </span>
                ) : null}
              </div>
            </div>
          </div>
        </div>

        <section
          className="land-views"
          aria-label={optionalText(c.viewsAccessible)}
          data-copy-group
        >
          {(hasText(c.viewsHeading) || hasText(c.viewsIntro)) && (
            <header className="land-views-head" data-copy-group>
              {hasText(c.viewsHeading) ? <h2 className="land-h2">{c.viewsHeading}</h2> : null}
              {hasText(c.viewsIntro) ? <p className="land-intro">{c.viewsIntro}</p> : null}
            </header>
          )}
          <div className="land-grid">
            {figures.map((figure) => (
              <figure
                key={figure.key}
                className={`land-view${figure.wide ? ' land-view-wide' : ''}`}
                tabIndex={0}
              >
                <div className="land-view-head">
                  <span className="land-balloon" aria-hidden="true"></span>
                  <div className="land-view-titles" data-copy-group>
                    {hasText(figure.title) ? (
                      <div className="land-view-title">{figure.title}</div>
                    ) : null}
                    {hasText(figure.meta) ? (
                      <div className="land-view-meta">{figure.meta}</div>
                    ) : null}
                  </div>
                </div>
                <div className={`land-view-frame land-view-frame-${figure.key}`}>
                  <img src={figure.src} alt={optionalText(figure.alt) ?? ''} loading="lazy" />
                </div>
                {hasText(figure.caption) ? <figcaption>{figure.caption}</figcaption> : null}
              </figure>
            ))}
          </div>
        </section>

        <div className="land-signoff">
          <div className="land-signoff-text" data-copy-group>
            {hasText(c.signOff) ? <div className="land-signoff-title">{c.signOff}</div> : null}
            {hasText(viewerStrings.webMain.filesStayInThisBrowserNothingIs) ? (
              <div className="land-note">
                {viewerStrings.webMain.filesStayInThisBrowserNothingIs}
              </div>
            ) : null}
          </div>
          <div className="land-cta">{upload}</div>
        </div>

        <footer className="land-block">
          <div className="land-block-cell" data-copy-group>
            {hasText(c.blockTitleLabel) ? (
              <span className="land-block-label">{c.blockTitleLabel}</span>
            ) : null}
            {hasText(c.blockTitle) ? (
              <span className="land-block-title">{c.blockTitle}</span>
            ) : null}
          </div>
          <div className="land-block-cell" data-copy-group>
            {hasText(c.blockDrawnByLabel) ? (
              <span className="land-block-label">{c.blockDrawnByLabel}</span>
            ) : null}
            {hasText(c.blockDrawnBy) ? <span>{c.blockDrawnBy}</span> : null}
          </div>
          <div className="land-block-cell" data-copy-group>
            {hasText(c.blockDataDateLabel) ? (
              <span className="land-block-label">{c.blockDataDateLabel}</span>
            ) : null}
            {hasText(c.blockDataDate) ? <span>{c.blockDataDate}</span> : null}
          </div>
          <div className="land-block-cell" data-copy-group>
            {hasText(c.blockScaleLabel) ? (
              <span className="land-block-label">{c.blockScaleLabel}</span>
            ) : null}
            {hasText(c.blockScale) ? <span>{c.blockScale}</span> : null}
          </div>
          <div className="land-block-foot" data-copy-group>
            {hasText(viewerStrings.webMain.browserLocalAnalysisCiscoCUCMCDRAnd) ||
            hasText(viewerStrings.webMain.callRecordingCom) ? (
              <span>
                {viewerStrings.webMain.browserLocalAnalysisCiscoCUCMCDRAnd}
                {hasText(viewerStrings.webMain.callRecordingCom) ? (
                  <a href="https://call-recording.com/">{viewerStrings.webMain.callRecordingCom}</a>
                ) : null}
              </span>
            ) : null}
            {hasText(c.syntheticNote) ? <span>{c.syntheticNote}</span> : null}
            {hasText(c.blockSheet) ? <span>{c.blockSheet}</span> : null}
          </div>
        </footer>
      </div>
    </section>
  );
}
